import { FieldWrapper } from './fieldWrapper';
import { AntInput, AntTextArea } from '../../../ant/antInput';
import { ActionMode, isActionMode, Property } from '../models';
import React from 'react';
import { observer } from 'mobx-react';
import { makeAutoObservable } from 'mobx';
import { FormBuilderContext } from '../formBuilderContext';
import { FormBuilderStore } from '../formBuilderStore';
import { AntInputNumber } from 'controls/react/ant/antInputNumber';
import { AntFlex } from 'controls/react/ant/antFlex';
import { AntButton } from 'controls/react/ant/antButton';
import { openModal } from 'controls/react/ant/antModal';
import { Section } from 'controls/react/layout/section';
import { useStore } from 'core/react/useStore';
import { i18n } from 'core/localization';

class CETextInputStore {
	popupText = '';

	constructor(
		public formStore: FormBuilderStore,
		public property: Property,
	) {
		makeAutoObservable(this);
	}

	destroy() {}

	get hasAction() {
		return isActionMode(this.property.actionMode);
	}

	get queryDisabled() {
		return this.formStore.propertyDisabled(this.property) || this.formStore.propertyLoading(this.property);
	}

	get queryTitle() {
		return this.property.actionMode === ActionMode.Indirect
			? i18n('Query and show result in popup')
			: i18n('Query and set result to field');
	}

	get placeholder() {
		const { caption, placeholder } = this.property;
		return placeholder ? `${caption} - ${placeholder}` : undefined;
	}

	onBlur = () => {
		void this.formStore.onPropertyBlur(this.property);
	};

	setPopupText = (text: string) => {
		this.popupText = text;
	};

	query = async () => {
		const data = await this.formStore.queryTextInput(this.property);
		if (data === undefined) return;

		if (this.property.actionMode === ActionMode.Indirect) {
			this.openResultPopup(data);
		} else {
			this.formStore.onPropertyValueChange(this.property, data);
		}
	};

	openResultPopup = (data: string) => {
		this.popupText = data;
		const modal = openModal(
			{
				title: this.property.caption,
				mode: 'update',
				width: 600,
				height: 400,
				onOk: () => {
					this.formStore.onPropertyValueChange(this.property, this.popupText);
					modal.close();
				},
			},
			<Section childrenPadding>
				<div>
					{i18n(
						'Query result can be edited. On update it will replace current content of {0}.',
						this.property.caption,
					)}
				</div>
				<AntTextArea defaultValue={data} style={{ flexGrow: 1 }} onChange={this.setPopupText} />
			</Section>,
		);
	};
}

export const CETextInput = observer(({ property }: { property: Property }) => {
	const formStore = React.useContext(FormBuilderContext);
	const store = useStore(() => new CETextInputStore(formStore, property));

	const field = (
		<FieldWrapper property={property} placeholder={store.placeholder}>
			{property.multiLine && <AntTextArea onBlur={store.onBlur} autoSize={{ minRows: 1, maxRows: 3 }} />}
			{property.numeric && <AntInputNumber onBlur={store.onBlur} />}
			{!property.multiLine && !property.numeric && (
				<AntInput type={property.password ? 'password' : 'text'} onBlur={store.onBlur} />
			)}
		</FieldWrapper>
	);

	if (!store.hasAction) {
		return field;
	}

	return (
		<AntFlex gap="10px" align="stretch">
			<div style={{ flexGrow: 1 }}>{field}</div>
			<AntButton
				type="primary"
				style={{ height: 38 }}
				disabled={store.queryDisabled}
				title={store.queryTitle}
				onClick={() => void store.query()}
			>
				{i18n('Run query')}
			</AntButton>
		</AntFlex>
	);
});
